// src/components/DashboardLayout.tsx
import React from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  CssBaseline,
  Button,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MainSideBar from "./MainSideBar";
import NotificationCenter from "./common/NotificationCenter";
import logo from "../Dutch-Tech-Inc.jpg";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { APP_VERSION } from "../version";

const drawerWidth = 240;

const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const handleDrawerToggle = () => setMobileOpen((o) => !o);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const goSettings = () => navigate("/settings");
  const goAccount = () => navigate("/account");

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <CssBaseline />

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: "rgba(255,255,255,0.92)",
          color: "text.primary",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar
          sx={{
            gap: { xs: 0.5, sm: 1 },
            px: { xs: 1, sm: 2 },
          }}
        >
          {/* Hamburger only on mobile */}
          <IconButton
            color="inherit"
            aria-label="open navigation"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: { xs: 0.5, sm: 1 }, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              minWidth: 0,
              flexShrink: 1,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{
                height: { xs: 30, sm: 36, md: 40 },
                mr: { xs: 0.5, sm: 1 },
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                pb: "2px",
                display: { xs: "none", sm: "inline" },
              }}
            >
              v{APP_VERSION}
            </Typography>
          </Box>

          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.25, sm: 1 },
            }}
          >
            <NotificationCenter />
            <Tooltip title="My Account">
              <IconButton
                aria-label="account"
                onClick={goAccount}
                size={isMobile ? "medium" : "large"}
              >
                <AccountCircleIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Settings">
              <IconButton
                aria-label="settings"
                onClick={goSettings}
                size={isMobile ? "medium" : "large"}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            {isMobile ? (
              <Tooltip title="Logout">
                <IconButton
                  aria-label="logout"
                  onClick={handleLogout}
                  color="primary"
                  size="medium"
                >
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleLogout}
                size="small"
                sx={{ ml: 0.5 }}
              >
                Logout
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Responsive navigation: temporary on mobile, permanent on desktop */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="main navigation"
      >
        {/* Mobile drawer */}
        <MainSideBar
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          drawerWidth={drawerWidth}
          sx={{ display: { xs: "block", md: "none" } }}
        />
        {/* Desktop drawer */}
        <MainSideBar
          variant="permanent"
          open
          drawerWidth={drawerWidth}
          sx={{ display: { xs: "none", md: "block" } }}
        />
      </Box>

      {/* Main content: offset for AppBar height + responsive padding */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.25, sm: 2, md: 3 },
          minWidth: 0,
          width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
          maxWidth: "100%",
          overflowX: "hidden",
        }}
      >
        {/* Spacer so content starts below the AppBar */}
        <Toolbar />

        {children}
      </Box>
    </Box>
  );
};

export default DashboardLayout;
